import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../../store';
import BillingSdk, { Billing } from "@smash-sdk/billing/01-2022";
import { AsyncState } from '../../shared/models/async.interface';
import { Transfer } from '@smash-sdk/transfer/10-2019';

interface PlanUsage {
    dataUsage: number,
    dataUsageLimit?: number,
    endDate: string,
    plan: string,
    id: string,
    price: number,
    automaticRenew: boolean,
    currency: 'eur' | 'usd',
    status: string,
}

export interface PlanUsageState extends AsyncState {
    planUsage: PlanUsage | null;
}

export const getPlanUsage = createAsyncThunk(
    'planUsage/getPlanUsage',
    async (_, thunkApi) => {
        try {
            const billing = new Billing();
            const { subscription } = await billing.getSubscription();
            const { limits } = await billing.listSubscriptionLimits();
            const transfer = new Transfer();
            const { usage } = await transfer.getUsage();
            const { dataInOut } = limits.services.transfer;
            return {
                planUsage: {
                    dataUsage: usage.bytesTotal,
                    dataUsageLimit: dataInOut?.max ? dataInOut.max : undefined,
                    endDate: subscription.endDate,
                    id: subscription.plan.id,
                    plan: subscription.plan.name,
                    price: subscription.plan.priceExclTax,
                    currency: subscription.plan.currency,
                    status: subscription.status,
                    automaticRenew: subscription.automaticRenew,
                }
            };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const unsubscribeSubscription = createAsyncThunk(
    'planUsage/unsubscribeSubscription',
    async (input: BillingSdk.UnsubscribeSubscriptionInput, thunkApi) => {
        try {
            const billing = new Billing();
            const { subscription } = await billing.unsubscribeSubscription(input);
            const { planUsage }: any = thunkApi.getState();//TODO FIX ME type
            return { planUsage: { ...planUsage.planUsage, automaticRenew: subscription.automaticRenew } }
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    });

export const initialState: PlanUsageState = {
    planUsage: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
}

export const planUsageSlice = createSlice({
    name: 'planUsage',
    initialState,
    reducers: {
        resetPlanUsage: () => ({
            planUsage: null,
            isLoading: false,
            isSuccess: false,
            isError: false,
        })
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPlanUsage.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(getPlanUsage.fulfilled, (state, action) => {
                state.isSuccess = true;
                state.isLoading = false;
                state.planUsage = action.payload.planUsage;
            })
            .addCase(getPlanUsage.rejected, (state) => {
                state.isSuccess = false;
                state.isLoading = false;
                state.isError = true;
                state.planUsage = null;
            })
            .addCase(unsubscribeSubscription.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isError = false;
            })
            .addCase(unsubscribeSubscription.fulfilled, (state, action) => {
                state.isSuccess = true;
                state.isLoading = false;
                state.planUsage = action.payload.planUsage;
            })
            .addCase(unsubscribeSubscription.rejected, (state) => {
                state.isSuccess = false;
                state.isLoading = false;
                state.isError = true;
                state.planUsage = null;
            })
    }
});


export const { resetPlanUsage } = planUsageSlice.actions;

export const planUsageState = (state: RootState) => state.planUsage;

export default planUsageSlice.reducer;